import React, { useState, useEffect, useRef } from "react";
import { X, RotateCcw } from "lucide-react";
import {
  FLAG_REGISTRY,
  setFlag,
  resetAllFlags,
  reloadForFlags,
} from "../featureFlags";

/**
 * <FeatureFlagsPanel> — modal for flipping runtime feature flags.
 *
 * Reads current values from localStorage (falling back to defaults) so
 * the panel always shows the state the app WOULD render with on reload.
 * When the user toggles anything, a "Reload to apply" banner appears
 * with a button — nothing changes until they explicitly reload.
 *
 * Wired from the sidebar footer popover ("Feature flags" menu item).
 * Intended for internal use during Phase 2 design-system rollout.
 */
export function FeatureFlagsPanel({ onClose }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(
      FLAG_REGISTRY.map(({ name, default: def }) => [name, readInitial(name, def)])
    )
  );
  const [dirty, setDirty] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const set = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setFlag(name, value);
    setDirty(true);
  };

  const resetAll = () => {
    resetAllFlags();
    setValues(
      Object.fromEntries(
        FLAG_REGISTRY.map(({ name, default: def }) => [name, def])
      )
    );
    setDirty(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Feature flags"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,10,10,0.32)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        padding: 24,
      }}
    >
      <div
        ref={cardRef}
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 12,
          boxShadow: "0 24px 48px -12px rgba(10,10,10,0.20)",
          width: "100%",
          maxWidth: 520,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: "var(--text-primary)",
                letterSpacing: "-0.01em",
              }}
            >
              Feature flags
            </h2>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Runtime overrides. Reload to apply.
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={iconBtnStyle}
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 20px" }}>
          {FLAG_REGISTRY.map(({ name, label, description, default: def }) => (
            <FlagRow
              key={name}
              label={label}
              description={description}
              defaultValue={def}
              value={values[name]}
              onChange={(v) => set(name, v)}
            />
          ))}
        </div>

        {/* Footer — reset + reload */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: dirty ? "rgba(11,87,164,0.04)" : "transparent",
          }}
        >
          <button onClick={resetAll} style={secondaryBtnStyle}>
            <RotateCcw size={12} strokeWidth={1.75} />
            Reset to defaults
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {dirty && (
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Changes pending
              </span>
            )}
            <button
              onClick={reloadForFlags}
              disabled={!dirty}
              style={{
                ...primaryBtnStyle,
                opacity: dirty ? 1 : 0.5,
                cursor: dirty ? "pointer" : "not-allowed",
              }}
            >
              Reload to apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// One flag row — label + description + toggle + "default" chip when overridden.
const FlagRow = ({ label, description, defaultValue, value, onChange }) => {
  const overridden = value !== defaultValue;
  return (
    <div
      style={{
        padding: "14px 0",
        borderTop: "1px solid var(--border-hairline)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--text-primary)",
            }}
          >
            {label}
          </span>
          {overridden && (
            <span
              style={{
                fontSize: 10,
                padding: "1px 6px",
                borderRadius: 4,
                background: "rgba(11,87,164,0.10)",
                color: "#0B57A4",
                fontWeight: 600,
                letterSpacing: 0,
              }}
            >
              Override
            </span>
          )}
        </div>
        {description && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.4 }}>
            {description}
          </div>
        )}
      </div>
      <Toggle checked={value} onChange={onChange} />
    </div>
  );
};

// iOS-style toggle. Cobalt when on, neutral when off. 32×18px.
const Toggle = ({ checked, onChange }) => (
  <button
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    style={{
      width: 32,
      height: 18,
      borderRadius: 999,
      border: "none",
      padding: 0,
      background: checked ? "#0B57A4" : "rgba(0,0,0,0.16)",
      cursor: "pointer",
      position: "relative",
      transition: "background 160ms cubic-bezier(0.4, 0, 0.2, 1)",
      flexShrink: 0,
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 2,
        left: checked ? 16 : 2,
        width: 14,
        height: 14,
        borderRadius: 999,
        background: "#FFFFFF",
        boxShadow: "0 1px 2px rgba(10,10,10,0.20)",
        transition: "left 160ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    />
  </button>
);

const readInitial = (name, def) => {
  try {
    const raw = window.localStorage.getItem("idz.flag." + name);
    if (raw === null) return def;
    return raw === "true";
  } catch {
    return def;
  }
};

const iconBtnStyle = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  borderRadius: 6,
  background: "transparent",
  color: "var(--text-muted)",
  cursor: "pointer",
  padding: 0,
};

const primaryBtnStyle = {
  height: 32,
  padding: "0 14px",
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "inherit",
  color: "#FFFFFF",
  background: "#0B57A4",
  border: "1px solid #0B57A4",
  borderRadius: 6,
  cursor: "pointer",
};

const secondaryBtnStyle = {
  height: 32,
  padding: "0 12px",
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "inherit",
  color: "var(--text-muted)",
  background: "transparent",
  border: "1px solid var(--border-subtle)",
  borderRadius: 6,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};
